import React from 'react';
import { Link } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';

interface GuideSection {
  title: string;
  description: string;
  link: string;
  linkLabel: string;
  farmOnly?: boolean;
  retailOnly?: boolean;
}

const farmGuides: GuideSection[] = [
  {
    title: 'Getting Started',
    description:
      'Farmhand is your digital farm assistant. Start by creating a seasonal plan, adding your batches, and recording your first voice note. Everything flows from there.',
    link: '/portal/farmhand/get-started',
    linkLabel: 'View Getting Started Guide',
  },
  {
    title: 'Recording Voice Notes',
    description:
      'Tap the red microphone button and speak naturally. Say things like "collected 342 eggs today" or "lost 2 birds in house 3." Laura will sort your notes into the right categories automatically.',
    link: '/portal/farmhand',
    linkLabel: 'Go to Dashboard',
  },
  {
    title: 'Understanding Your Dashboard',
    description:
      'Your dashboard shows active plans, current batches, upcoming deliveries, and weather alerts. Use it as your daily check-in to see what needs attention.',
    link: '/portal/farmhand',
    linkLabel: 'View Dashboard',
  },
  {
    title: 'Managing Batches & Entries',
    description:
      'Each batch tracks a group of animals from arrival to harvest. Tap into a batch to see its log entries, edit recent notes (within 48 hours), or review the full history.',
    link: '/portal/farmhand/batches',
    linkLabel: 'View Your Batches',
  },
  {
    title: 'Egg Watch',
    description:
      'Egg Watch uses weather data and your flock history to forecast egg production and flag heat stress risks. Set up your sensitivity profile to get personalized alerts.',
    link: '/portal/farmhand/egg-watch',
    linkLabel: 'Open Egg Watch',
    farmOnly: true,
  },
  {
    title: 'Farm Map & Predator Tracking',
    description:
      'Pin your houses, fields, and infrastructure on the farm map. Log predator sightings to track patterns over time and protect your flock.',
    link: '/portal/farmmap',
    linkLabel: 'Open Farm Map',
    farmOnly: true,
  },
  {
    title: 'Your Prices & Sales',
    description:
      'Set your egg or product prices in Settings. Laura will use them when you log sales by voice — just say "sold 10 dozen at the usual price" and she handles the math.',
    link: '/portal/settings',
    linkLabel: 'Manage Prices',
  },
  {
    title: 'Documents & Uploads',
    description:
      'Upload price sheets, contracts, or farm records. Laura can read price sheets and learn your pricing automatically. Documents from Red Bridge also appear here.',
    link: '/portal/my-documents',
    linkLabel: 'My Documents',
  },
  {
    title: 'Offline Mode',
    description:
      'No signal in the barn? No problem. Voice notes recorded offline are saved to your device and sync automatically when you reconnect.',
    link: '/portal/farmhand',
    linkLabel: 'Go to Dashboard',
  },
  {
    title: 'Tasks & Team',
    description:
      'Create tasks for yourself or your crew. Voice notes can auto-complete tasks — just mention what you did and Laura marks it done.',
    link: '/portal/farm-tasks',
    linkLabel: 'View Tasks',
    farmOnly: true,
  },
  {
    title: 'Settings & Preferences',
    description:
      'Manage your notification preferences, Laura voice settings, email alerts, and account details. Turn on Laura\'s voice to hear responses spoken aloud.',
    link: '/portal/settings',
    linkLabel: 'Open Settings',
  },
];

const retailGuides: GuideSection[] = [
  {
    title: 'Getting Started',
    description:
      'Welcome to the Red Bridge Farm Feed Co. portal. From here you can place orders, view invoices, manage your team, and access marketing resources.',
    link: '/portal/dashboard',
    linkLabel: 'Go to Dashboard',
  },
  {
    title: 'Placing Orders',
    description:
      'Browse available products and place orders directly through the portal. Track order status and view your full order history anytime.',
    link: '/portal/place-order',
    linkLabel: 'Place an Order',
  },
  {
    title: 'Billing & Payments',
    description:
      'View and pay invoices, check your account balance, and manage tax exemption certificates all in one place.',
    link: '/portal/billing',
    linkLabel: 'View Billing',
  },
  {
    title: 'Bridge Club',
    description:
      'Manage your store\'s Bridge Club loyalty program. View member activity, track rewards, and access promotional materials.',
    link: '/portal/bridge-club',
    linkLabel: 'Bridge Club Dashboard',
  },
  {
    title: 'Resources & Marketing',
    description:
      'Download product sheets, promotional materials, signage, and videos to help sell Red Bridge products in your store.',
    link: '/portal/resources',
    linkLabel: 'View Resources',
  },
  {
    title: 'Documents',
    description:
      'Access contracts, agreements, and other documents shared with your account. Download or view them anytime.',
    link: '/portal/my-documents',
    linkLabel: 'My Documents',
  },
  {
    title: 'Reports',
    description:
      'View order history reports, annual summaries, and reorder suggestions to help you stock the right products.',
    link: '/portal/reports',
    linkLabel: 'View Reports',
  },
  {
    title: 'Team Management',
    description:
      'Add team members so multiple people at your store can access the portal. Control permissions for ordering and billing.',
    link: '/portal/team',
    linkLabel: 'Manage Team',
  },
  {
    title: 'Settings & Preferences',
    description:
      'Update your notification preferences, email settings, and account details.',
    link: '/portal/settings',
    linkLabel: 'Open Settings',
  },
];

interface PortalHelpGuidesProps {
  embedded?: boolean;
}

const PortalHelpGuides: React.FC<PortalHelpGuidesProps> = ({ embedded = false }) => {
  const { user } = usePortalAuth();
  const isRetailPartner = user?.customer?.customer_type === 'RETAIL_PARTNER';
  const guides = isRetailPartner ? retailGuides : farmGuides;

  const content = (
    <>
      {!embedded && <TutorialAutoStart pageKey="help-guides" />}

      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
              Help & Guides
            </h1>
            <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
              {isRetailPartner
                ? 'Quick reference for everything in your portal'
                : 'Quick reference for all Farmhand features'}
            </p>
          </div>
          <TutorialHelpButton pageKey="help-guides" />
        </div>
      )}

      {embedded && (
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          {isRetailPartner
            ? 'Quick reference for everything in your portal'
            : 'Quick reference for all Farmhand features'}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {guides.map((guide) => (
          <div
            key={guide.title}
            style={{
              background: '#fff',
              border: '1px solid #e8e0d4',
              borderRadius: 10,
              padding: '16px 18px',
            }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 600, color: '#111827', margin: '0 0 6px 0' }}>
              {guide.title}
            </h3>
            <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, margin: '0 0 10px 0' }}>
              {guide.description}
            </p>
            <Link
              to={guide.link}
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: '#6C8062',
                textDecoration: 'none',
              }}
            >
              {guide.linkLabel} &rarr;
            </Link>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 24,
        padding: '16px 18px',
        background: '#f9fafb',
        borderRadius: 10,
        border: '1px solid #e8e0d4',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
          Need more help? Contact us at{' '}
          <a href="mailto:office@theredbridgefarm.com" style={{ color: '#6C8062', textDecoration: 'none', fontWeight: 500 }}>
            office@theredbridgefarm.com
          </a>
        </p>
      </div>
    </>
  );

  if (embedded) return content;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      {content}
    </div>
  );
};

export default PortalHelpGuides;
